import {FC, useState} from 'react';
import {toast} from 'react-toastify';
import {VerdocsButton, VerdocsSpinner} from '@verdocs/web-sdk-react';
import {type IBrand, createBrand, VerdocsEndpoint} from '@verdocs/js-sdk';
import {BRAND_KEY_PATTERN, BRAND_THEME_TAGS, BRAND_THEMES, type IBrandTheme, slugifyKey} from '../Views/Settings/BrandThemes';
import {track} from '../Managers/Analytics';
import {AppState} from '../AppState';
import {Dialog} from './Dialog';

interface StartFromThemeDialogProps {
  onClose: () => void;
  onCreated: (brand: IBrand) => void;
}

const ThemeCard: FC<{theme: IBrandTheme; selected: boolean; onSelect: () => void}> = ({theme, selected, onSelect}) => (
  <button
    onClick={onSelect}
    className={`overflow-hidden rounded-xl border-[1.5px] bg-white text-left transition-all ${
      selected ? 'border-indigo-600 ring-[3px] ring-indigo-100' : 'border-gray-200 hover:border-indigo-300 hover:shadow-md'
    }`}>
    <div className="bg-gray-50 px-3 pt-3">
      <div className="overflow-hidden rounded-t-lg border border-b-0 border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-2 px-2.5 py-2" style={{background: theme.headerBg, color: theme.headerFg}}>
          <span className="truncate text-[9.5px] font-semibold opacity-95">Agreement.pdf</span>
          <span className="whitespace-nowrap rounded px-2 py-1 text-[8.5px] font-bold" style={{background: theme.btnBg, color: theme.btnFg}}>
            Next
          </span>
        </div>
        <div className="px-2.5 pb-3.5 pt-3">
          <span className="mb-1 block h-1 rounded-sm bg-gray-200" />
          <span className="mb-1 block h-1 w-3/5 rounded-sm bg-gray-200" />
          <span className="mt-2 block h-3 rounded border border-dashed" style={{borderColor: theme.accent, background: `${theme.accent}1A`}} />
          <span className="mt-2.5 block h-2 w-11 rounded" style={{background: theme.primary}} />
        </div>
      </div>
    </div>
    <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2.5">
      <span className="text-xs font-semibold text-gray-900">{theme.name}</span>
      <span className="text-[10.5px] capitalize text-gray-400">{theme.tag}</span>
    </div>
  </button>
);

export const StartFromThemeDialog: FC<StartFromThemeDialogProps> = ({onClose, onCreated}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const [activeTag, setActiveTag] = useState('all');
  const [selected, setSelected] = useState<IBrandTheme | null>(null);
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyDirty, setKeyDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const visibleThemes = BRAND_THEMES.filter((t) => activeTag === 'all' || t.tag === activeTag);
  const canCreate = !!selected && BRAND_KEY_PATTERN.test(key) && !submitting;

  const handleSelect = (theme: IBrandTheme) => {
    setSelected(theme);
    if (!keyDirty) setKey(slugifyKey(theme.name));
  };

  const handleCreate = () => {
    if (!selected || !canCreate) return;
    setSubmitting(true);
    createBrand(VerdocsEndpoint.getDefault(), organizationId, {
      key,
      name: name.trim() || undefined,
      primary_color: selected.primary,
      secondary_color: selected.accent,
    })
      .then((r) => {
        setSubmitting(false);
        toast.success('Brand created');
        track('brand_created', {org_id: organizationId, source: 'theme'});
        onCreated(r);
      })
      .catch((e: any) => {
        setSubmitting(false);
        toast.error(`Error creating brand: ${e.response?.data?.error || e.message}`);
      });
  };

  return (
    <Dialog size="lg" onCancel={onClose}>
      <div className="flex min-h-0 flex-1 flex-col text-base">
        <div className="mb-3 flex items-center">
          <h1 className="sans-serif flex-1 text-xl font-bold text-[#33364b]">Start from a theme</h1>
          <button onClick={onClose} className="rounded-md px-2 py-0.5 text-xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            &times;
          </button>
        </div>
        <p className="mb-4 max-w-[640px] text-[13.5px] leading-relaxed text-gray-500">
          Every theme pairs a header with an action button that contrasts against it, because these colors paint UI chrome, not content. Pick a
          starting point; you can refine every color afterward.
        </p>

        <div className="mb-4 flex flex-wrap gap-2">
          {BRAND_THEME_TAGS.map((tag) => (
            <button
              key={tag}
              onClick={() => setActiveTag(tag)}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium capitalize transition-colors ${
                tag === activeTag ? 'border-indigo-200 bg-indigo-50 text-indigo-600' : 'border-transparent bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {tag}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pb-4 pr-1">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
            {visibleThemes.map((theme) => (
              <ThemeCard key={theme.name} theme={theme} selected={selected?.name === theme.name} onSelect={() => handleSelect(theme)} />
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
          <div className="flex gap-1">
            {(selected ? [selected.headerBg, selected.btnBg, selected.primary, selected.accent] : []).map((c, i) => (
              <i key={i} className="h-[17px] w-[17px] rounded border border-black/10" style={{background: c}} />
            ))}
          </div>
          <span className={`text-[13px] ${selected ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>{selected?.name || 'No theme selected'}</span>
          <div className="flex-1" />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Brand name (optional)"
            className="w-44 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-indigo-400 focus:outline-none"
          />
          <input
            value={key}
            onChange={(e) => {
              setKey(slugifyKey(e.target.value));
              setKeyDirty(true);
            }}
            placeholder="key"
            className="w-32 rounded-lg border border-gray-300 px-2.5 py-1.5 font-mono text-sm focus:border-indigo-400 focus:outline-none"
          />
          <VerdocsButton size="small" label="Create Brand" disabled={!canCreate} onClick={handleCreate} />
          {submitting && <VerdocsSpinner mode="dark" />}
        </div>
      </div>
    </Dialog>
  );
};
